/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `segment_id` message.
 *
 * Unique Identification data for a segment file
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class SegmentIdMesg : Mesg {
    public constructor() : super("SegmentId", Profile.MesgNum.SEGMENT_ID)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** name. Friendly name assigned to segment */
    public var name: String?
        get() = getFieldValue(NAME_FIELD_NUM) as? String
        set(value) = setFieldValue(NAME_FIELD_NUM, value)

    /** uuid. UUID of the segment */
    public var uuid: String?
        get() = getFieldValue(UUID_FIELD_NUM) as? String
        set(value) = setFieldValue(UUID_FIELD_NUM, value)

    /** sport. Sport associated with the segment */
    public var sport: Sport?
        get() = (getFieldValue(SPORT_FIELD_NUM) as? UByte)?.let { Sport.fromValue(it) }
        set(value) = setFieldValue(SPORT_FIELD_NUM, value?.value)

    /** enabled. Segment enabled for evaluation */
    public var enabled: Boolean?
        get() = (getFieldValue(ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** user_profile_primary_key. Primary key of the user that created the segment */
    public var userProfilePrimaryKey: UInt?
        get() = getFieldValue(USER_PROFILE_PRIMARY_KEY_FIELD_NUM) as? UInt
        set(value) = setFieldValue(USER_PROFILE_PRIMARY_KEY_FIELD_NUM, value)

    /** device_id. ID of the device that created the segment */
    public var deviceId: UInt?
        get() = getFieldValue(DEVICE_ID_FIELD_NUM) as? UInt
        set(value) = setFieldValue(DEVICE_ID_FIELD_NUM, value)

    /** default_race_leader. Index for the Leader Board entry selected as the default race participant */
    public var defaultRaceLeader: UByte?
        get() = getFieldValue(DEFAULT_RACE_LEADER_FIELD_NUM) as? UByte
        set(value) = setFieldValue(DEFAULT_RACE_LEADER_FIELD_NUM, value)

    /** delete_status. Indicates if any segments should be deleted */
    public var deleteStatus: SegmentDeleteStatus?
        get() = (getFieldValue(DELETE_STATUS_FIELD_NUM) as? UByte)?.let { SegmentDeleteStatus.fromValue(it) }
        set(value) = setFieldValue(DELETE_STATUS_FIELD_NUM, value?.value)

    /** selection_type. Indicates how the segment was selected to be sent to the device */
    public var selectionType: SegmentSelectionType?
        get() = (getFieldValue(SELECTION_TYPE_FIELD_NUM) as? UByte)?.let { SegmentSelectionType.fromValue(it) }
        set(value) = setFieldValue(SELECTION_TYPE_FIELD_NUM, value?.value)

    public companion object {
        public const val NAME_FIELD_NUM: UByte = 0u
        public const val UUID_FIELD_NUM: UByte = 1u
        public const val SPORT_FIELD_NUM: UByte = 2u
        public const val ENABLED_FIELD_NUM: UByte = 3u
        public const val USER_PROFILE_PRIMARY_KEY_FIELD_NUM: UByte = 4u
        public const val DEVICE_ID_FIELD_NUM: UByte = 5u
        public const val DEFAULT_RACE_LEADER_FIELD_NUM: UByte = 6u
        public const val DELETE_STATUS_FIELD_NUM: UByte = 7u
        public const val SELECTION_TYPE_FIELD_NUM: UByte = 8u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[0] = Field("Name", 0u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[1] = Field("Uuid", 1u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[2] = Field("Sport", 2u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[3] = Field("Enabled", 3u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[4] = Field("UserProfilePrimaryKey", 4u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[5] = Field("DeviceId", 5u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[6] = Field("DefaultRaceLeader", 6u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[7] = Field("DeleteStatus", 7u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[8] = Field("SelectionType", 8u, BaseType.ENUM, 1.0, 0.0, "", false)
            return fields
        }
    }
}
